/**
 * UI locale primitives. The UI locale is a path prefix on every route
 * (`/en/login`, `/de/library`, ...). Currently only English is shipped,
 * but the URL structure is in place so translations can be added without
 * another URL migration.
 */

export const SUPPORTED_UI_LOCALES = ['en'] as const;
export type UILocale = typeof SUPPORTED_UI_LOCALES[number];

export const DEFAULT_UI_LOCALE: UILocale = 'en';
export const UI_LOCALE_STORAGE_KEY = 'sc-ui-locale';

export const EXPLORE_LANG_STORAGE_KEY = 'sc-explore-lang';

const ISO2 = /^[a-z]{2}$/;

export function isSupportedUILocale(value: string | null | undefined): value is UILocale {
  return !!value && (SUPPORTED_UI_LOCALES as readonly string[]).includes(value);
}

export function isIso2(value: string | null | undefined): value is string {
  return !!value && ISO2.test(value);
}

/** Language code from navigator (lowercased, first two chars) — not validated. */
export function browserLanguage(): string {
  const raw =
    typeof navigator !== 'undefined' && navigator.language
      ? navigator.language
      : 'en';
  return raw.toLowerCase().slice(0, 2);
}

/**
 * Preferred UI locale resolution:
 *   URL param > localStorage > navigator.language (if supported) > default.
 * URL param takes precedence so shared links win.
 */
export function resolveUILocale(urlParam: string | null | undefined): UILocale {
  if (isSupportedUILocale(urlParam)) return urlParam;
  try {
    const stored =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(UI_LOCALE_STORAGE_KEY)
        : null;
    if (isSupportedUILocale(stored)) return stored;
  } catch {
    // ignore storage errors
  }
  const nav = browserLanguage();
  if (isSupportedUILocale(nav)) return nav;
  return DEFAULT_UI_LOCALE;
}

/**
 * Persist user's UI locale pick. No-op on environments without localStorage.
 */
export function persistUILocale(locale: UILocale): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(UI_LOCALE_STORAGE_KEY, locale);
    }
  } catch {
    // ignore storage errors
  }
}

/** Prefix any app path with the current UI locale. */
export function withLocale(locale: UILocale, path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (normalized === '/') return `/${locale}`;
  return `/${locale}${normalized}`;
}
